import { useState } from 'react'
import type { ReactNode } from 'react'
import styled from 'styled-components'
import { IconChevronDown, IconChevronUp } from 'suomifi-icons'

/**
 * Kollapsoituva sektio. Klikkaus otsikkoon avaa/sulkee sektion (workshop C9).
 *
 * - KayttajaKortilla `defaultOpen={false}` — kaikki kortit kiinni oletuksena (D4)
 * - TukipyyntoNakymässä `defaultOpen={true}` — kortit auki oletuksena
 *
 * Saavutettavuus: <button> haitari-otsikkona, aria-expanded ja aria-controls,
 * Enter/Space avaa-sulkee natiivisti button-elementin kautta.
 */

interface Props {
  otsikko: string
  merkinta?: string
  /** True = sektio on auki oletuksena. KayttajaKortilla false, tukipyynnössä true. */
  defaultOpen?: boolean
  children: ReactNode
}

const Sektio = styled.section`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
`

const Otsikko = styled.button`
  width: 100%;
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.s};
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.depthLight3};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: -2px;
  }
`

const OtsikkoTeksti = styled.span`
  flex: 1;
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
`

const Merkinta = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.brandBase};

  & > svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const Sisalto = styled.div`
  padding: ${({ theme }) => theme.spacing.m};
`

let idCounter = 0
function generateId(): string {
  idCounter += 1
  return `sektio-haitari-${idCounter}`
}

export default function SektioHaitari({
  otsikko,
  merkinta,
  defaultOpen = false,
  children,
}: Props) {
  const [auki, setAuki] = useState(defaultOpen)
  const [sisaltoId] = useState(generateId)

  return (
    <Sektio>
      <Otsikko
        type="button"
        onClick={() => setAuki(prev => !prev)}
        aria-expanded={auki}
        aria-controls={sisaltoId}
      >
        <OtsikkoTeksti>{otsikko}</OtsikkoTeksti>
        {merkinta && <Merkinta>{merkinta}</Merkinta>}
        <IconWrap aria-hidden>
          {auki ? <IconChevronUp /> : <IconChevronDown />}
        </IconWrap>
      </Otsikko>
      {auki && <Sisalto id={sisaltoId}>{children}</Sisalto>}
    </Sektio>
  )
}
